/**
 * Anti-Fraud Service - Detecting Punch Manipulation
 *
 * Validates:
 * 1. GPS accuracy (reject high-error readings)
 * 2. Mock location detection (Android emulators, spoofing apps)
 * 3. Device binding (same device for punches)
 * 4. Velocity checks (impossible travel times between punches)
 * 5. Timezone consistency
 */

import { db, publicarHuellaDispositivo } from '../db/database';

// =============================================================================
// Types
// =============================================================================

export interface LocationValidation {
  isValid: boolean;
  latitude: number;
  longitude: number;
  accuracy: number;
  warnings: string[];
  flags: LocationFlag[];
}

export type LocationFlag =
  | 'LOW_ACCURACY'      // GPS accuracy > 100m
  | 'MOCK_LOCATION'     // Detected mock/fake location
  | 'IMPOSSIBLE_SPEED'  // Traveled too fast between punches
  | 'TIMEZONE_MISMATCH' // Device timezone doesn't match location
  | 'EMULATOR_DETECTED' // Running on emulator
  | 'VPN_SUSPECTED';    // IP location doesn't match GPS

export interface DeviceValidation {
  isValid: boolean;
  fingerprint: string;
  isBound: boolean;
  warnings: string[];
}

// =============================================================================
// Constants
// =============================================================================

const GPS_ACCURACY_THRESHOLD_METERS = 100; // Reject if accuracy > 100m
const GPS_ACCURACY_WARNING_METERS = 50;    // Warn if accuracy > 50m
const MAX_SPEED_KMH = 200;                 // Max realistic travel speed
const EARTH_RADIUS_KM = 6371;

// =============================================================================
// Location Validation
// =============================================================================

/**
 * Validate a GPS location for potential fraud
 */
export async function validateLocation(
  latitude: number,
  longitude: number,
  accuracy: number,
  userId: number
): Promise<LocationValidation> {
  const warnings: string[] = [];
  const flags: LocationFlag[] = [];
  let isValid = true;

  // 1. Check GPS accuracy
  if (accuracy > GPS_ACCURACY_THRESHOLD_METERS) {
    flags.push('LOW_ACCURACY');
    warnings.push(`Precisión GPS baja: ${Math.round(accuracy)}m`);
    // Precisión mala es física, no fraude: NO se bloquea el fichaje (doctrina
    // del backend: sin GPS fiable no se descarta, se marca y lo resuelve la
    // geovalla del servidor / el responsable). El flag LOW_ACCURACY viaja igual.
  } else if (accuracy > GPS_ACCURACY_WARNING_METERS) {
    warnings.push(`Precisión GPS moderada: ${Math.round(accuracy)}m`);
  }

  // 2. Check for mock location (Android)
  const mockDetected = detectMockLocation(accuracy);
  if (mockDetected) {
    flags.push('MOCK_LOCATION');
    warnings.push('Posible ubicación simulada detectada');
    isValid = false;
  }

  // 3. Check for impossible travel speed
  const speedCheck = await checkTravelSpeed(latitude, longitude, userId);
  if (speedCheck.isImpossible) {
    flags.push('IMPOSSIBLE_SPEED');
    warnings.push(
      `Velocidad imposible: ${Math.round(speedCheck.speedKmh)} km/h desde el último fichaje`
    );
    // La velocidad imposible ya no BLOQUEA el fichaje (ronda 3, r2b18[4]).
    // Con el histórico de otro (dispositivo compartido) o un GPS que salta,
    // el trabajador no podía fichar y su marca real no quedaba NI en local.
    // Doctrina del propio código: «sin GPS fiable no se descarta, se marca»
    // — la flag viaja con el evento y lo mira la revisión del responsable.
  }

  // 4. Check timezone consistency
  const timezoneCheck = checkTimezone(latitude, longitude);
  if (!timezoneCheck.isConsistent) {
    flags.push('TIMEZONE_MISMATCH');
    warnings.push(`Zona horaria no coincide con ubicación`);
    // Don't invalidate, just flag (might be traveling)
  }

  // 5. Check for emulator
  const emulatorDetected = detectEmulator();
  if (emulatorDetected) {
    flags.push('EMULATOR_DETECTED');
    warnings.push('Dispositivo emulado detectado');
    // Don't invalidate for dev purposes, but flag
  }

  return {
    isValid,
    latitude,
    longitude,
    accuracy,
    warnings,
    flags,
  };
}

/**
 * Detect if running on a mock location.
 *
 * La web NO expone el flag `isMock` de Android: eso sólo lo ve una app
 * nativa (Capacitor/React Native), y hasta que exista, lo único barato y
 * real que ofrece la Geolocation API es una precisión imposible (<= 0),
 * que es lo que devuelven varios emuladores y apps de GPS falso.
 *
 * ⚠️ HONESTIDAD DEL LEDGER: sin flag MOCK_LOCATION no hay certificado de
 * ubicación real — sólo significa que ESTA heurística no disparó. El
 * responsable no debe leer la ausencia de señales como control hecho.
 */
function detectMockLocation(accuracy?: number): boolean {
  return accuracy != null && accuracy <= 0;
}

/**
 * Check if travel speed between last punch and current location is possible
 */
async function checkTravelSpeed(
  currentLat: number,
  currentLon: number,
  userId: number
): Promise<{ isImpossible: boolean; speedKmh: number }> {
  // ⚠️ Esto era `db.punchEvents.where('latitude').above(0).last()`, y
  // `latitude` NO está indexado en ninguna versión del esquema: Dexie lanza
  // SchemaError al construir la colección, con la tabla vacía o llena. La
  // excepción subía por validateLocation → validatePunch → handlePunch, donde
  // el catch la trataba como fallo de validación: **el fichaje no se guardaba
  // en IndexedDB y la cola no recibía nada**. O sea que cualquier trabajador
  // que concediera el permiso de ubicación —el que la propia app le insta a
  // dar— no podía fichar en presencial. Sólo fichaban los de teletrabajo (no
  // mandan ubicación) y los que tenían el GPS denegado.
  //
  // Se busca por el índice que SÍ existe (`timestamp`) y se filtra en memoria:
  // son unas pocas filas y la alternativa es una migración de esquema para un
  // índice que sólo usa esta comprobación.
  const recientes = await db.punchEvents
    .orderBy('timestamp')
    .reverse()
    .limit(50)
    .toArray();
  // El histórico es de QUIEN ficha, no del dispositivo (ronda 3, r2b18[4]):
  // en un móvil compartido, el último punto con coordenadas pertenecía a
  // OTRO trabajador y la «velocidad imposible» describía un viaje que el
  // que fichaba no había hecho.
  const lastPunch = recientes.find(
    (e) => e.userId === userId && e.latitude != null && e.longitude != null
  );

  if (!lastPunch || !lastPunch.latitude || !lastPunch.longitude) {
    return { isImpossible: false, speedKmh: 0 };
  }

  // Calculate distance using Haversine formula
  const distanceKm = haversineDistance(
    lastPunch.latitude,
    lastPunch.longitude,
    currentLat,
    currentLon
  );

  // Calculate time difference in hours
  const timeHours =
    (Date.now() - lastPunch.timestamp.getTime()) / (1000 * 60 * 60);

  if (timeHours <= 0) {
    return { isImpossible: false, speedKmh: 0 };
  }

  const speedKmh = distanceKm / timeHours;

  return {
    isImpossible: speedKmh > MAX_SPEED_KMH,
    speedKmh,
  };
}

/**
 * Calculate distance between two points using Haversine formula
 */
function haversineDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return EARTH_RADIUS_KM * c;
}

function toRad(deg: number): number {
  return deg * (Math.PI / 180);
}

/**
 * Check if device timezone is consistent with GPS location
 */
function checkTimezone(
  _latitude: number, // Reserved for future timezone database lookup
  longitude: number
): { isConsistent: boolean; expectedTimezone: string; deviceTimezone: string } {
  const deviceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Simple heuristic based on longitude
  // In production, use a proper timezone database (e.g., geo-tz library)
  const hourOffset = Math.round(longitude / 15);
  const expectedOffset = new Date().getTimezoneOffset() / -60;

  // Allow 2-hour tolerance for timezone boundaries and DST
  const isConsistent = Math.abs(hourOffset - expectedOffset) <= 2;

  return {
    isConsistent,
    expectedTimezone: `UTC${hourOffset >= 0 ? '+' : ''}${hourOffset}`,
    deviceTimezone,
  };
}

/**
 * Detect if running on an emulator
 */
function detectEmulator(): boolean {
  // Check for common emulator indicators
  const userAgent = navigator.userAgent.toLowerCase();

  const emulatorIndicators = [
    'android sdk',
    'sdk_gphone',
    'emulator',
    'simulator',
    'generic',
    'genymotion',
    'bluestacks',
    'nox',
    'memu',
    'ldplayer',
  ];

  return emulatorIndicators.some((indicator) =>
    userAgent.includes(indicator)
  );
}

// =============================================================================
// Device Binding
// =============================================================================

/**
 * Validate device and check if it's bound to the user
 */
export async function validateDevice(): Promise<DeviceValidation> {
  const warnings: string[] = [];

  // Get or create device fingerprint
  let device = await db.deviceInfo.orderBy('id').first();

  if (!device) {
    // First time - create fingerprint
    const fingerprint = generateDeviceFingerprint();
    await db.deviceInfo.add({
      fingerprint,
      lastUsed: new Date(),
    });
    // La huella donde http.ts la lee para X-Device-Fingerprint (ronda 4,
    // r2b18[4]): la clave existía y nadie la escribía.
    publicarHuellaDispositivo(fingerprint);

    return {
      isValid: true,
      fingerprint,
      isBound: false, // New device, not yet bound
      warnings: ['Nuevo dispositivo detectado'],
    };
  }

  // Update last used
  await db.deviceInfo.update(device.id!, { lastUsed: new Date() });

  // Check if device is bound to a user
  const isBound = !!device.userId;

  if (!isBound) {
    warnings.push('Dispositivo no vinculado a usuario');
  }

  return {
    isValid: true,
    fingerprint: device.fingerprint,
    isBound,
    warnings,
  };
}

/**
 * Bind device to current user
 */
export async function bindDeviceToUser(userId: number): Promise<void> {
  const device = await db.deviceInfo.orderBy('id').first();

  if (!device) {
    // Primera vinculación en un dispositivo virgen (ronda 3, r2b18[5]): la
    // fila `deviceInfo` la creaban `validateDevice`/`createPunchEvent`,
    // ambos POSTERIORES al login, así que el `bindDeviceToUser` del arranque
    // lanzaba 'No device found' — promesa rechazada sin gestionar desde
    // `setUser` y todo el primer día con «Dispositivo no vinculado» en cada
    // fichaje. Se crea ya vinculada a quien acaba de entrar.
    const fingerprint = generateDeviceFingerprint();
    await db.deviceInfo.add({
      fingerprint,
      userId,
      boundAt: new Date(),
      lastUsed: new Date(),
    });
    publicarHuellaDispositivo(fingerprint);
    console.log(`[AntiFraud] Device ${fingerprint} created and bound to user ${userId}`);
    return;
  }

  await db.deviceInfo.update(device.id!, {
    userId,
    boundAt: new Date(),
  });
  publicarHuellaDispositivo(device.fingerprint);

  console.log(`[AntiFraud] Device ${device.fingerprint} bound to user ${userId}`);
}

/**
 * Generate a browser-based device fingerprint
 */
function generateDeviceFingerprint(): string {
  const components: string[] = [];

  // Screen info
  components.push(`${screen.width}x${screen.height}x${screen.colorDepth}`);

  // Timezone
  components.push(Intl.DateTimeFormat().resolvedOptions().timeZone);

  // Language
  components.push(navigator.language);

  // Platform
  components.push(navigator.platform || 'unknown');

  // Hardware concurrency (CPU cores)
  components.push(String(navigator.hardwareConcurrency || 0));

  // Touch support
  components.push(String('ontouchstart' in window));

  // Canvas fingerprint (simplified)
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Kakuat fingerprint test', 2, 2);
      components.push(canvas.toDataURL().slice(-50));
    }
  } catch {
    components.push('canvas-error');
  }

  // WebGL info (simplified)
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl');
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        components.push(gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || 'unknown');
        components.push(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || 'unknown');
      }
    }
  } catch {
    components.push('webgl-error');
  }

  // Hash the components
  const fingerprint = hashString(components.join('|'));

  return fingerprint;
}

/**
 * Simple hash function for fingerprinting.
 *
 * SIN componente aleatorio (ronda 4, r2b18[4]): antes concatenaba
 * `crypto.randomUUID().slice(0,8)`, así que dos generaciones en el MISMO
 * dispositivo daban huellas distintas y el control "device binding" no
 * vinculaba el dispositivo ni en el tiempo ni entre usuarios. La huella es
 * ahora estable por dispositivo; si alguien borra la fila `deviceInfo`,
 * el mismo hardware vuelve a producir la misma huella.
 */
function hashString(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  // Convert to hex and pad
  const hex = Math.abs(hash).toString(16).padStart(8, '0');

  return hex;
}

// =============================================================================
// Composite Validation
// =============================================================================

export interface PunchValidation {
  isValid: boolean;
  location?: LocationValidation;
  device: DeviceValidation;
  allWarnings: string[];
  allFlags: LocationFlag[];
}

/**
 * Run all anti-fraud validations before a punch
 */
export async function validatePunch(
  userId: number,
  latitude?: number,
  longitude?: number,
  accuracy?: number
): Promise<PunchValidation> {
  const allWarnings: string[] = [];
  const allFlags: LocationFlag[] = [];
  let isValid = true;

  // Validate device
  const device = await validateDevice();
  if (!device.isValid) {
    isValid = false;
  }
  allWarnings.push(...device.warnings);

  // Validate location (if provided)
  let location: LocationValidation | undefined;
  if (latitude !== undefined && longitude !== undefined && accuracy !== undefined) {
    location = await validateLocation(latitude, longitude, accuracy, userId);
    if (!location.isValid) {
      isValid = false;
    }
    allWarnings.push(...location.warnings);
    allFlags.push(...location.flags);
  }

  return {
    isValid,
    location,
    device,
    allWarnings,
    allFlags,
  };
}
